import React, { MouseEvent, useState } from 'react';

import { components, FormatOptionLabelMeta, MenuListProps } from 'react-select';

import { CreateTimeCodeRequestType } from 'api';
import deleteIcon from 'assets/images/delete-icon.svg';
import { CommonModalType, ReturnComponentType } from 'common';
import './ModalInspectionSettings.scss';
import { useAppSelector, useTypedDispatch } from 'hooks';
import {
  fetchTimeCodes,
  TimeCodeType,
  editTimeCode,
  fetchTags,
  deleteTag,
  createTag,
} from 'state';
import { selectCertainInspection, selectTags } from 'state/selectors';
import { Button, InputForm, Select } from 'ui-kit';
import { ISelectOption } from 'ui-kit/Select/Select';
import { checkIsGoodStatus, getOptions } from 'utils';

type ModalInspectionSettingsType = CommonModalType & {
  timeCode?: TimeCodeType;
};

export const ModalInspectionSettings = ({
  showModal,
  setShowModal,
  timeCode,
}: ModalInspectionSettingsType): ReturnComponentType => {
  const dispatch = useTypedDispatch();
  const inspection = useAppSelector(selectCertainInspection);
  const tags = useAppSelector(selectTags);
  const [newTagName, setNewTagName] = useState('');
  const tagsOptions = getOptions(tags, 'id', 'title').map(file => {
    return { ...file, image: deleteIcon };
  });
  const [data, setData] = useState<Omit<CreateTimeCodeRequestType, 'fileId'>>({
    timecodeStart: timeCode?.timecodeStart ?? '',
    timecodeEnd: timeCode?.timecodeEnd ?? '',
    tagId: timeCode?.tagId ?? '',
  });

  if (!showModal) return null;
  const closeInspectionSettingsModal = (e: MouseEvent) => {
    e.stopPropagation();
    setShowModal(false);
  };

  const onInputChange = (e: string) => {
    setNewTagName(e);
  };
  const onAddNewTagClick = async () => {
    const status = await dispatch(createTag({ title: newTagName }));

    if (checkIsGoodStatus(status)) {
      setNewTagName('');
      dispatch(fetchTags());
    }
  };
  const onSaveSettingsClick = async (e: MouseEvent) => {
    e.stopPropagation();
    if (timeCode) {
      const status = await dispatch(editTimeCode({ ...data, id: timeCode?.id }));

      if (status?.status) {
        dispatch(fetchTimeCodes(inspection.id));
        setShowModal(false);
      }
    }
  };
  const onChangeTag = (e: ISelectOption) => {
    setData({ ...data, tagId: e.value });
  };
  const onBoxClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const onDeleteTagClick = async (e: MouseEvent, id: string) => {
    const status = await dispatch(deleteTag(id));

    e.stopPropagation();

    if (checkIsGoodStatus(status)) {
      setNewTagName('');
      dispatch(fetchTags());
    }
  };
  const formatOptionLabel = (
    file: ISelectOption,
    { context }: FormatOptionLabelMeta<ISelectOption>,
  ) => {
    return (
      <div className="InspectionHeader_Select_Row">
        <span>{file?.label}</span>
        {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
        {context === 'menu' && (
          <img
            src={file?.image}
            alt="delete-icon"
            onClickCapture={e => onDeleteTagClick(e, file?.value)}
          />
        )}
      </div>
    );
  };
  // eslint-disable-next-line react/no-unstable-nested-components
  const MenuList = (props: MenuListProps) => {
    return (
      <components.MenuList {...props}>
        <div className="MenuListHeader">
          <span className="MenuListHeader_Title">{newTagName}</span>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <span className="MenuListHeader_Add" onClick={onAddNewTagClick}>
            + Add
          </span>
        </div>
        {/* eslint-disable-next-line react/destructuring-assignment */}
        {props.children}
      </components.MenuList>
    );
  };

  return (
    <>
      {showModal && (
        <div className="ModalInspectionSettings">
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            className="ModalInspectionSettings_Container"
            onClick={closeInspectionSettingsModal}
          />
        </div>
      )}
      <div className="ModalInspectionSettings">
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div className="ModalInspectionSettings_Box" onClick={onBoxClick}>
          <section>
            <div className="ModalInspectionSettings_Box_Title">Time code starts</div>
            <InputForm
              value={data.timecodeStart}
              onChange={e => setData({ ...data, timecodeStart: e.currentTarget.value })}
            />
          </section>
          <section>
            <div className="ModalInspectionSettings_Box_Title">Time code ends</div>
            <InputForm
              value={data.timecodeEnd}
              onChange={e => setData({ ...data, timecodeEnd: e.currentTarget.value })}
            />
          </section>
          <div className="ModalInspectionSettings_Box_Title">Property address</div>
          <h5 className="ModalInspectionSettings_Box_Address">
            property address # 1{/* timeCode?.propertyAddress */}
          </h5>

          <section>
            <div className="ModalInspectionSettings_Box_Title">Tag</div>
            <Select
              options={tagsOptions}
              components={{ MenuList }}
              className="ModalInspectionSettings_Box_Select"
              // @ts-ignore
              onChange={e => onChangeTag(e)}
              defaultValue={tagsOptions?.find(tag => tag.value === timeCode?.tagId)}
              formatOptionLabel={formatOptionLabel}
              onInputChange={e => onInputChange(e)}
              inputValue={newTagName}
            />
          </section>
          <Button onClick={onSaveSettingsClick}>Save</Button>
        </div>
      </div>
    </>
  );
};
